import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSnackBar } from '@sbv-clients/ui-shared';
import {
  getBlobErrorMessage,
  getApiErrorMessage,
} from '@sbv-clients/util-misc';

import {
  AttachmentsApiAttachmentsFacilityIdEmployeeIdDeleteRequest,
  AttachmentsApiAttachmentsFacilityIdEmployeeIdDownloadFolderGetRequest,
  AttachmentsApiAttachmentsFacilityIdEmployeeIdDownloadGetRequest,
  AttachmentsApiAttachmentsFacilityIdEmployeeIdDownloadSelectionPostRequest,
  AttachmentsApiAttachmentsFacilityIdEmployeeIdGetRequest,
  AttachmentsApiAttachmentsFacilityIdEmployeeIdPostRequest,
  AttachmentsApiAttachmentsFacilityIdEmployeeIdRenamePutRequest,
  useApi,
} from '../index';
import { AttachmentsCacheKeys } from './util/cache-keys';

export const useGetAttachments = (
  params: AttachmentsApiAttachmentsFacilityIdEmployeeIdGetRequest,
  config: { enabled?: boolean } = {}
) => {
  const api = useApi();
  const { facilityId, employeeId, folderPath } = params;
  const { enabled = true } = config;

  return useQuery({
    queryKey: [
      AttachmentsCacheKeys.ATTACHMENTS,
      facilityId,
      employeeId,
      folderPath ?? '',
    ],
    queryFn: () =>
      api.attachmentsApi
        .attachmentsFacilityIdEmployeeIdGet({
          facilityId,
          employeeId,
          folderPath,
        })
        .then((res) => res.data),
    ...config,
    enabled: enabled && !!facilityId && employeeId != null,
    refetchOnWindowFocus: false,
  });
};

export const useCreateFolder = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(
    (params: AttachmentsApiAttachmentsFacilityIdEmployeeIdPostRequest) =>
      api.attachmentsApi.attachmentsFacilityIdEmployeeIdPost(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([AttachmentsCacheKeys.ATTACHMENTS]);
      },
      onError: () => {
        notifyError('Failed to create folder');
      },
    }
  );
};

export const useDeleteAttachment = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(
    (params: AttachmentsApiAttachmentsFacilityIdEmployeeIdDeleteRequest) =>
      api.attachmentsApi.attachmentsFacilityIdEmployeeIdDelete(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([AttachmentsCacheKeys.ATTACHMENTS]);
        notifySuccess('Deleted successfully');
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error) ?? 'Failed to delete');
      },
    }
  );
};

export const useRenameAttachment = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(
    (params: AttachmentsApiAttachmentsFacilityIdEmployeeIdRenamePutRequest) =>
      api.attachmentsApi.attachmentsFacilityIdEmployeeIdRenamePut(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([AttachmentsCacheKeys.ATTACHMENTS]);
        notifySuccess('Renamed successfully');
      },
      onError: (error) => {
        notifyError(getApiErrorMessage(error) ?? 'Failed to rename');
      },
    }
  );
};

export const useDownloadAttachment = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(
    async (
      params: AttachmentsApiAttachmentsFacilityIdEmployeeIdDownloadGetRequest & {
        fileName: string;
      }
    ) => {
      const { fileName, ...rest } = params;
      const response =
        await api.attachmentsApi.attachmentsFacilityIdEmployeeIdDownloadGet(
          rest,
          { responseType: 'blob' }
        );
      const url = URL.createObjectURL(
        new Blob([response.data as any as BlobPart])
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    },
    {
      onError: (error) => {
        notifyError(getApiErrorMessage(error) ?? 'Failed to download file');
      },
    }
  );
};

export const useDownloadFolder = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(
    async (
      params: AttachmentsApiAttachmentsFacilityIdEmployeeIdDownloadFolderGetRequest & {
        folderName: string;
      }
    ) => {
      const { folderName, ...rest } = params;
      const response =
        await api.attachmentsApi.attachmentsFacilityIdEmployeeIdDownloadFolderGet(
          rest,
          { responseType: 'blob' }
        );
      const url = URL.createObjectURL(
        new Blob([response.data as any as BlobPart])
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${folderName}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    },
    {
      onError: (error) => {
        notifyError(getApiErrorMessage(error) ?? 'Failed to download folder');
      },
    }
  );
};

export const useDownloadSelection = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  return useMutation(
    async (
      params: AttachmentsApiAttachmentsFacilityIdEmployeeIdDownloadSelectionPostRequest & {
        fileName: string;
      }
    ) => {
      const { fileName, ...rest } = params;
      const response =
        await api.attachmentsApi.attachmentsFacilityIdEmployeeIdDownloadSelectionPost(
          rest,
          { responseType: 'blob' }
        );
      const url = URL.createObjectURL(
        new Blob([response.data as any as BlobPart])
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    },
    {
      onError: async (error) => {
        const message = await getBlobErrorMessage(error);
        notifyError(message ?? 'Failed to download selection');
      },
    }
  );
};

export const useUploadFile = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useSnackBar();

  return useMutation(
    (params: AttachmentsApiAttachmentsFacilityIdEmployeeIdPostRequest) =>
      api.attachmentsApi.attachmentsFacilityIdEmployeeIdPost(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([AttachmentsCacheKeys.ATTACHMENTS]);
      },
      onError: () => {
        notifyError('Failed to upload file');
      },
    }
  );
};
